import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { Conditions } from '@vsolv/packages/conditions/domain';
import { Property, PropertySet } from '@vsolv/packages/properties/domain';
import { DIMENSION_VALUE_NAMESPACE } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, map, switchMap } from 'rxjs';
import { RatingMatrixService } from '../../services';

export interface PropertyWithSameType {
  property: Property.Model<Property.PropertyType> | Conditions.Property;
  children?: PropertyWithSameType[];
  isType: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-condition-value-property-picker',
  templateUrl: './condition-value-property-picker.component.html',
  standalone: false,
})
export class ConditionValuePropertyPickerComponent {
  @Input() disabled = false;

  @PropertyListener('propertySet') propertySet$ = new BehaviorSubject<PropertySet.Model | null>(null);
  @Input() propertySet: PropertySet.Model | null = null;

  @PropertyListener('type') type$ = new BehaviorSubject<Conditions.PropertyType>(Conditions.PropertyType.TEXT);
  @Input() type!: Conditions.PropertyType;

  @PropertyListener('format') format$ = new BehaviorSubject<string | null>(null);
  @Input() format: string | null = null;

  /** Also offer scalar asset dimensions, not just properties. */
  @PropertyListener('includeScalarDimensions') includeScalarDimensions$ = new BehaviorSubject<boolean>(false);
  @Input() includeScalarDimensions = false;

  overlayOpen = false;

  @PropertyListener('selectedPropertyPath') selectedPropertyPath$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() selectedPropertyPath?: string | null;

  @Input() value: {
    property: Property.Model<Property.PropertyType> | Conditions.Property;
    propertyPath: string;
  } | null = null;

  @Output() selectionChanged = new EventEmitter<{
    property: {
      property: Property.Model<Property.PropertyType> | Conditions.Property;
      propertyPath: string;
    } | null;
  }>();

  constructor(private ratingMatrixSvc: RatingMatrixService) {}

  // Bumped when the picker opens so the dimension list is refetched (see refreshScalarDimensions).
  private refresh$ = new BehaviorSubject<void>(undefined);

  properties$ = combineLatest([
    this.propertySet$,
    this.type$,
    this.format$,
    this.includeScalarDimensions$,
    this.refresh$,
  ]).pipe(
    switchMap(async ([set, , , includeScalarDimensions]) => {
      if (!set) return [];

      const setProperties =
        set.properties
          ?.map(assignment => assignment.property)
          .filter((property): property is Property.Model<Property.PropertyType> => !!property) ?? [];

      const propertyEntries = this.mapProperties([...Conditions.getProperties(), ...setProperties]);

      if (!includeScalarDimensions) return propertyEntries;

      const dimensionEntries = await this.getScalarDimensionEntries(set.id);
      return [...propertyEntries, ...dimensionEntries];
    })
  );

  selectedProperty$ = combineLatest([this.properties$, this.selectedPropertyPath$]).pipe(
    map(([properties, selectedPropertyPath]) => {
      if (selectedPropertyPath && selectedPropertyPath !== this.value?.propertyPath) {
        const property = this.getProperty(
          selectedPropertyPath,
          properties.map(prop => prop.property)
        );
        if (property) {
          this.setValue({ property, propertyPath: selectedPropertyPath });
        }
      }
    })
  );

  private getProperty(
    propertyPath: string,
    properties: (Property.Model<Property.PropertyType> | Conditions.Property)[]
  ): Property.Model<Property.PropertyType> | Conditions.Property | undefined {
    for (const property of properties) {
      if (property.valueKey === propertyPath) {
        return property;
      } else if (propertyPath.startsWith(property.valueKey)) {
        return this.getProperty(
          propertyPath.replace(property.valueKey + '.', ''),
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          (property as Property.ObjectModel).properties?.map(assignment => assignment.property!) ?? []
        );
      }
    }
    return undefined;
  }

  setValue(
    value: { property: Property.Model<Property.PropertyType> | Conditions.Property; propertyPath: string } | null
  ) {
    this.value = value;
    this.selectionChanged.emit({ property: value });
  }

  open() {
    this.overlayOpen = true;
    this.refreshScalarDimensions();
  }

  close() {
    this.overlayOpen = false;
  }

  toggle() {
    this.overlayOpen = !this.overlayOpen;
    if (this.overlayOpen) this.refreshScalarDimensions();
  }

  // Dimensions can be created after the list was first cached — drop the cache and
  // refetch on open so newly-added ones show without a full page reload.
  private refreshScalarDimensions() {
    if (!this.includeScalarDimensions || !this.propertySet?.id) return;
    this.ratingMatrixSvc.invalidateDimensions(this.propertySet.id);
    this.refresh$.next(undefined);
  }

  isType(property: Property.Model<Property.PropertyType> | Conditions.Property) {
    if (property.type !== this.type) return false;
    if (!this.format) return true;

    const propertyFormat = property.config?.format ?? null;
    return propertyFormat === this.format;
  }

  containsType(property: Property.Model<Property.PropertyType> | Conditions.Property) {
    if (this.isType(property)) return true;

    if (property.type !== Property.PropertyType.OBJECT) return false;

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    for (const child of (property as Property.ObjectModel).properties!) {
      const containsType = this.containsType(child.property as Property.ObjectModel);
      if (containsType) return true;
    }

    return false;
  }

  private mapProperties(properties: (Property.Model<Property.PropertyType> | Conditions.Property)[]) {
    const validProperties: PropertyWithSameType[] = [];
    for (const property of properties) {
      if (this.isType(property)) {
        validProperties.push({ property: property as Property.ObjectModel, isType: true });
      } else if (this.containsType(property)) {
        const children = this.mapProperties(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          (property as Property.ObjectModel).properties!.map(assignment => assignment.property!)
        );
        validProperties.push({ property: property, children, isType: false });
      }
    }
    return validProperties;
  }

  // Scalar dimensions for the set, as picker entries. Each emits a `dimension.<id>`
  // path that resolves like a property at runtime.
  private async getScalarDimensionEntries(propertySetId: string): Promise<PropertyWithSameType[]> {
    try {
      const dimensions = await this.ratingMatrixSvc.listScalarDimensions(propertySetId);
      return dimensions.map(dimension => ({
        property: {
          id: dimension.id,
          type: this.type,
          name: dimension.title,
          valueKey: `${DIMENSION_VALUE_NAMESPACE}.${dimension.id}`,
          config: { format: this.format ?? null },
          isChild: false,
        } as unknown as Conditions.Property,
        isType: true,
      }));
    } catch {
      return [];
    }
  }
}
